/**
 * Split copy button for docs pages.
 *
 * Left button copies page markdown to clipboard.
 * Right dropdown offers copy, open as markdown, and llms.txt link.
 */

import { Check, ChevronDown, Copy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface DocsCopyButtonProps {
	/** Raw markdown content of the current page */
	markdown: string;
}

export function DocsCopyButton({ markdown }: DocsCopyButtonProps) {
	const [copied, setCopied] = useState(false);

	const copyToClipboard = () => {
		navigator.clipboard.writeText(markdown);
		setCopied(true);
		toast.success("Copied to clipboard");
		setTimeout(() => setCopied(false), 2000);
	};

	const openMarkdown = () => {
		const blob = new Blob([markdown], { type: "text/markdown" });
		const url = URL.createObjectURL(blob);
		window.open(url, "_blank");
		setTimeout(() => URL.revokeObjectURL(url), 1000);
	};

	return (
		<div className="flex items-center">
			<Button
				variant="outline"
				size="sm"
				className="rounded-r-none border-r-0"
				onClick={copyToClipboard}
			>
				{copied ? <Check className="size-4" /> : <Copy className="size-4" />}
				<span className="ml-1.5">{copied ? "Copied" : "Copy"}</span>
			</Button>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="outline" size="sm" className="rounded-l-none px-2">
						<ChevronDown className="size-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					<DropdownMenuItem onClick={copyToClipboard}>
						<div>
							<div className="font-medium">Copy page as Markdown</div>
							<div className="text-xs text-muted-foreground">Copy this page to clipboard</div>
						</div>
					</DropdownMenuItem>
					<DropdownMenuItem onClick={openMarkdown}>
						<div>
							<div className="font-medium">Open as Markdown</div>
							<div className="text-xs text-muted-foreground">View raw markdown in a new tab</div>
						</div>
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					<DropdownMenuItem asChild>
						<a href="/llms-full.txt" target="_blank" rel="noopener noreferrer">
							<div>
								<div className="font-medium">View all docs as Markdown</div>
								<div className="text-xs text-muted-foreground">
									Full documentation for LLMs (llms.txt)
								</div>
							</div>
						</a>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}
